/**
 * COSO Principle Drawer Component
 * 
 * Side panel for viewing and editing COSO principle narratives
 * Features:
 * - Displays principle title and description
 * - Rich Text Editor for narrative entry
 * - Export/Download functionality
 * - Save/Cancel actions with toast notifications
 * - API integration for saving content
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, message } from 'antd';
import ExportButton from '../../../components/common/ExportButton';
import styled from 'styled-components';
import {
  RichTextEditorComponent,
  Toolbar,
  HtmlEditor,
  QuickToolbar,
  Link,
  Inject
} from '@syncfusion/ej2-react-richtexteditor';
import RightContentDrawer from '../../../components/common/RightContentDrawer';
import ButtonGroup from '../../../components/common/ButtonGroup';
import { RootState, AppDispatch } from '../../../store';
import { updateNote } from '../../../store/slices/cosoComplianceSlice';
import { updateCOSOComplianceNote } from '../../../services/cosoComplianceService';
import { COSOPrinciple } from './cosoComplianceData';
import { htmlToPlainText } from '@/utils/exportUtils';

// ========== TYPE DEFINITIONS ==========

export interface COSOPrincipleDrawerProps {
  open: boolean;
  principle: COSOPrinciple | null;
  onClose: () => void;
  onSaveSuccess?: (principleNumber: number) => void;
}

// ========== STYLED COMPONENTS ==========

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 10px;
  margin-top: -20px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const RichTextEditorWrapper = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #D1D5DB;
  position: relative;
  overflow: visible;
  transition: border-color 0.3s;
  
  &:focus-within {
    border-color: #2563EB;
  }
  
  .e-richtexteditor {
    border: none;
    border-radius: 8px;
    font-family: Inter;
  }
  
  .e-richtexteditor .e-toolbar-wrapper {
    border-bottom: none;
  }
  
  .e-richtexteditor .e-rte-toolbar {
    background: white !important;
    border-bottom: 1px solid #E5E7EB;
    border-radius: 8px 8px 0 0;
    padding: 4px 8px;
    min-height: 44px;
  }
  
  .e-richtexteditor .e-toolbar {
    background: white !important;
    flex-wrap: wrap;
    overflow-x: auto;
    overflow-y: visible;
  }
  
  .e-richtexteditor .e-toolbar .e-toolbar-items {
    flex-wrap: wrap;
    background: white !important;
  }
  
  .e-richtexteditor .e-toolbar .e-toolbar-item {
    background: transparent !important;
  }
  
  .e-richtexteditor .e-rte-content {
    min-height: 200px;
    font-size: 14px;
    line-height: 20px;
    color: #1F2937;
    border-top: none;
  }
  
  .e-richtexteditor .e-rte-content .e-content {
    padding: 12px;
  }
  
  .e-toolbar-item {
    margin: 2px;
  }
  
  .e-toolbar-item .e-tbar-btn {
    color: #374151;
    border: none;
    background: transparent;
    padding: 6px 8px;
    min-width: 32px;
    height: 32px;
    border-radius: 4px;
  }
  
  .e-toolbar-item .e-tbar-btn:hover {
    background: #F3F4F6;
    color: #1F2937;
  }
  
  .e-toolbar-item .e-tbar-btn.e-active {
    background: #EFF6FF;
    color: #2563EB;
  }
  
  .e-toolbar .e-separator {
    border-color: #E5E7EB;
    margin: 0 4px;
  }
  
  .e-toolbar-item .e-dropdown-btn {
    color: #374151;
    border: none;
    background: transparent !important;
    padding: 6px 8px;
    height: 32px;
    border-radius: 4px;
  }
  
  .e-toolbar-item .e-dropdown-btn:hover {
    background: #F3F4F6 !important;
  }
  
  .e-richtexteditor .e-toolbar .e-tbar-btn-text,
  .e-richtexteditor .e-toolbar .e-btn-icon,
  .e-richtexteditor .e-toolbar .e-icons,
  .e-richtexteditor .e-toolbar-item .e-rte-dropdown-btn-text {
    background: transparent !important;
  }
  
  .e-richtexteditor .e-toolbar .e-toolbar-extended {
    display: none;
  }
  
  .e-richtexteditor .e-rte-srctextarea,
  .e-rte-source-code-textarea,
  .e-source-content {
    display: none !important;
  }
  
  .e-rte-inline-popup {
    display: none !important;
  }
  
  .e-richtexteditor .e-rte-resize {
    display: none !important;
  }
`;

// ========== MAIN COMPONENT ==========

const COSOPrincipleDrawer: React.FC<COSOPrincipleDrawerProps> = ({
  open,
  principle,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  // Get annual report ID from Redux
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  
  // Get notes from Redux to get current content
  const { notes } = useSelector((state: RootState) => state.cosoCompliance);
  
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const rteRef = useRef<RichTextEditorComponent>(null);

  // Load content when principle changes or drawer opens
  useEffect(() => {
    if (principle && open) {
      // Find content from Redux notes
      const note = notes.find(n => n.principleNumber === principle.number);
      const noteContent = note?.content || '';
      setContent(noteContent);
    }
  }, [principle, open, notes]);

  // Attach input event listener for real-time updates
  useEffect(() => {
    if (!open) return;
    
    const handleInput = () => {
      if (rteRef.current) {
        const currentValue = rteRef.current.getHtml() || '';
        setContent(currentValue);
      }
    };

    // Find the content editable element and attach listener
    const contentElement = document.querySelector('.e-richtexteditor .e-content');
    if (contentElement) {
      contentElement.addEventListener('input', handleInput);
      contentElement.addEventListener('keyup', handleInput);
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('input', handleInput);
        contentElement.removeEventListener('keyup', handleInput);
      }
    };
  }, [open]);

  // Handle change event as fallback (fires on blur)
  const handleContentChange = (args: any) => {
    setContent(args.value);
  };

  // Enable spellcheck after RTE is created
  const handleCreated = useCallback(() => {
    if (rteRef.current) {
      try {
        // For div mode (contenteditable)
        const editableElement = rteRef.current.contentModule?.getEditPanel?.();
        if (editableElement) {
          editableElement.setAttribute('spellcheck', 'true');
        }
        // For iframe mode
        const iframeDoc = rteRef.current.contentModule?.getDocument?.();
        if (iframeDoc?.body) {
          iframeDoc.body.setAttribute('spellcheck', 'true');
        }
      } catch (error) {
        console.warn('Could not enable spellcheck on RTE', error);
      }
    }
  }, []);

  const handleSave = async () => {
    if (!principle || !selectedReport?.annualReportId) {
      message.error('Unable to save: No annual report selected');
      return;
    }

    setLoading(true);
    try {
      // Call API to update
      await updateCOSOComplianceNote(
        selectedReport.annualReportId,
        principle.number,
        content
      );

      // Update Redux state
      dispatch(updateNote({
        principleNumber: principle.number,
        content,
      }));

      message.success(`Principle ${principle.number} has been updated.`);
      onClose();
    } catch (error: any) {
      console.error('Failed to save principle narrative:', error);
      message.error(error?.message || 'An error occurred while saving. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!principle) return;
    // Create a simple text export of the narrative
    const exportContent = `COSO Principle ${principle.number} - ${principle.title}\n\n${principle.description}\n\n---\n\n${htmlToPlainText(content)}`;
    const blob = new Blob([exportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `COSO_Principle_${principle.number}_Narrative.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success('Narrative exported successfully');
  };

  const handleClose = () => {
    onClose();
  };

  const drawerTitle = principle
    ? `COSO Principle ${principle.number} - Compliance Response`
    : 'COSO Principle';

  return (
    <RightContentDrawer
      open={open}
      title={drawerTitle}
      width={600}
      onClose={handleClose}
      footer={
        <ButtonGroup>
          <Button
            type="default"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={loading}
            disabled={!isAdmin}
          >
            {loading ? 'Saving...' : 'Save changes'}
          </Button>
        </ButtonGroup>
      }
    >
      <DrawerContent>
        {/* Export Button */}
        <HeaderSection>
          <ExportButton onClick={handleExport} title="Export narrative" />
        </HeaderSection>

        {/* Rich Text Editor */}
        <RichTextEditorWrapper>
          <RichTextEditorComponent
            ref={rteRef}
            value={content}
            change={handleContentChange}
            created={handleCreated}
            placeholder="Document how the organization is compliant with this COSO principle"
            toolbarSettings={{
              enable: true,
              enableFloating: false,
              items: [
                'Bold', 'Italic', 'Underline', 'StrikeThrough', '|',
                'CreateLink', '|',
                'UnorderedList', 'OrderedList'
              ]
            }}
            quickToolbarSettings={{
              enable: false
            }}
          >
            <Inject services={[Toolbar, HtmlEditor, QuickToolbar, Link]} />
          </RichTextEditorComponent>
        </RichTextEditorWrapper>
      </DrawerContent>
    </RightContentDrawer>
  );
};

export default COSOPrincipleDrawer;
